/**
 * Générateur — Automatisme : reconnaître un parallélogramme particulier à
 * partir de ses propriétés caractéristiques, notamment à partir des propriétés
 * de ses diagonales.
 * Attendu ID : 1123 | Niveau : 4ème | Domaine : Géométrie plane
 * Rubrique : AUTOMATISME (programme 2026, Parallélogrammes et translations 4e).
 * Labels (cartes exerciseur) :
 *   - Quelle est sa nature ?         (QCM : un parallélogramme codé → rectangle, losange, carré, rien de plus ;
 *     QCM planche : quatre parallélogrammes codés, lequel est certainement … ? — carte FUSIONNÉE, décision prof 2026-09-12)
 *   - La propriété utilisée          (QCM : quelle propriété du codage justifie la nature annoncée)
 *   - Vrai ou faux ?                 (V/F sur un parallélogramme codé : lecture du codage, conclusion)
 *   - Les parallélogrammes particuliers : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Parallélogrammes et translations 4e + grille P1-P16.
 * A. P1 : CM2 introduit (rectangle, losange, carré comme formes), 6e consolidé
 * (codages, angle droit, côtés égaux), 5e formalisé (parallélogrammes
 * particuliers DÉFINIS comme parallélogrammes et CARACTÉRISÉS par leurs
 * diagonales : de même longueur pour le rectangle, perpendiculaires pour le
 * losange, les deux pour le carré ; ou par un angle droit, deux côtés
 * consécutifs égaux), 4e automatisme. P2 : sens installés — la forme (« ça
 * ressemble à un rectangle »), le codage cycle 3 (gen 1080 : quatre angles
 * droits, quatre côtés égaux) ; sens 5e-4e — la propriété caractéristique dans
 * un parallélogramme (UN angle droit suffit, DEUX côtés consécutifs égaux
 * suffisent, les diagonales décident). Distracteurs : lire les diagonales
 * comme si elles disaient autre chose (même longueur → losange ?), croire
 * qu'un carré n'est pas un rectangle, conclure sans codage (allure). P3 :
 * aucun nombre. P4 : sans objet.
 * B. P5 : parallélogramme codé sur feuille blanche, diagonales tracées
 * quand elles sont codées. P6 : aucun instrument. P7 : aucune mesure (« seuls
 * les codages font foi », doctrine gen 1080). P8 : rectangle, losange, carré,
 * diagonales, perpendiculaires, côtés consécutifs.
 * C. P9 : lecture des codages (6e), parallélogrammes particuliers par codage
 * cycle 3 (1080), reconnaître un parallélogramme (1122). P10 : « d'après le
 * codage, que peut-on affirmer ? » — propriétés caractéristiques admises en
 * 5e. P11 : le carré est à la fois rectangle et losange (inclusions) ; les
 * diagonales de même longueur ne font pas un losange ; l'allure ne prouve
 * rien. P12 : rémanence forte.
 * D. P13 : leurres = nature voisine (rectangle ↔ losange), le carré confondu
 * avec l'une de ses deux natures, « rien de plus » quand seul le
 * parallélogramme est codé, allure trompeuse (un vrai rectangle codé
 * seulement parallélogramme). P14 : aucun calcul. P15 : UNICITÉ — le lecteur
 * de codage est une fonction pure ; la réponse attendue est la nature la plus
 * précise ; pour la planche, la seule figure de la nature demandée (un carré
 * n'est jamais sur une planche où l'on cherche un rectangle ou un losange).
 * P16 : QCM à quatre natures ; QCM planche numérotée 1 à 4 ; V/F sans crochets.
 * INVARIANTS : chaque figure est ANNONCÉE parallélogramme (dessinée par
 * vecteurs) ; le dessin a AU MOINS la nature conclue : plus précise sept fois sur dix pour
 * « rien de plus », une fois sur deux pour rectangle et losange (allure trompeuse, revue 2026-09-10 : un vrai carré
 * codé « un angle droit » se lit rectangle) ; data-motif / data-conclusion
 * pour les solveurs ; V/F mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { dessineQuad, milieu, dist, codageDroitInterieur } from '../../5eme/parallelogrammes/svgParallelo';

const ATTENDU_ID = 1123;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Quelle est sa nature ?';
const L_PROPRIETE = 'La propriété utilisée';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Les parallélogrammes particuliers : vrai ou faux ?';

// ─── Le lecteur de codage (fonction pure) ──────────────────────────────────
// codes : { diagEgales, diagPerp, angleDroit, consecEgaux } (booléens)
const lireCodage = (c) => {
    const rect = !!(c.diagEgales || c.angleDroit);
    const los = !!(c.diagPerp || c.consecEgaux);
    if (rect && los) return 'carre';
    if (rect) return 'rectangle';
    if (los) return 'losange';
    return 'sansplus';
};
const NOM = { rectangle: 'un rectangle', losange: 'un losange', carre: 'un carré', sansplus: 'on ne peut rien affirmer de plus' };
const PHRASE = {
    diagEgales: 'Ses diagonales ont la même longueur.',
    diagPerp: 'Ses diagonales sont perpendiculaires.',
    angleDroit: 'Il a un angle droit.',
    consecEgaux: 'Il a deux côtés consécutifs de même longueur.',
};
// Les deux propriétés d'un carré, dans l'ordre des clés du motif.
const PHRASE2 = {
    'diagEgales+diagPerp': 'Ses diagonales ont la même longueur et sont perpendiculaires.',
    'angleDroit+consecEgaux': 'Il a un angle droit et deux côtés consécutifs de même longueur.',
    'diagEgales+consecEgaux': 'Ses diagonales ont la même longueur et il a deux côtés consécutifs de même longueur.',
    'diagPerp+angleDroit': 'Ses diagonales sont perpendiculaires et il a un angle droit.',
};

// Vivier FERMÉ de motifs. codes = ce qui est codé ; extra = codage du parallélogramme (décor : milieu des diagonales, côtés opposés).
const MOTIFS = [
    { id: 'diag_egales', codes: { diagEgales: true } },
    { id: 'diag_perp', codes: { diagPerp: true } },
    { id: 'diag_egales_perp', codes: { diagEgales: true, diagPerp: true } },
    { id: 'angle', codes: { angleDroit: true } },
    { id: 'consec', codes: { consecEgaux: true } },
    { id: 'angle_consec', codes: { angleDroit: true, consecEgaux: true } },
    { id: 'diag_egales_consec', codes: { diagEgales: true, consecEgaux: true } },
    { id: 'diag_perp_angle', codes: { diagPerp: true, angleDroit: true } },
    { id: 'sansplus_milieu', codes: {}, extra: 'milieu' },
    { id: 'sansplus_opposes', codes: {}, extra: 'opposes' },
    { id: 'sansplus_vide', codes: {}, extra: 'diagonales' },
    { id: 'sansplus_paralleles', codes: {}, extra: 'paralleles' },
].map((m) => ({ ...m, conclusion: lireCodage(m.codes) }));
const PARTICULIERS = MOTIFS.filter((m) => m.conclusion !== 'sansplus');
const SANSPLUS = MOTIFS.filter((m) => m.conclusion === 'sansplus');
const parNature = (n) => MOTIFS.filter((m) => m.conclusion === n);

// ─── Formes (repère écran, construites par vecteurs) ───────────────────────
const rot = (P, deg) => { const r = (deg * Math.PI) / 180; return { x: P.x * Math.cos(r) - P.y * Math.sin(r), y: P.x * Math.sin(r) + P.y * Math.cos(r) }; };
/** Sommets d'un parallélogramme de la nature demandée (A, B, C, D dans l'ordre), penché au hasard. */
const sommets = (nature) => {
    let u; let v;
    if (nature === 'rectangle') { u = { x: randInt(56, 72), y: 0 }; v = { x: 0, y: -randInt(32, 44) }; }
    else if (nature === 'carre') { const c = randInt(44, 54); u = { x: c, y: 0 }; v = { x: 0, y: -c }; }
    else if (nature === 'losange') { const a = randInt(34, 44); const b = randInt(20, 26); u = { x: a, y: -b }; v = { x: a, y: b }; }
    else { u = { x: randInt(54, 70), y: 0 }; v = { x: randomChoice([-18, -12, 12, 16, 20]), y: -randInt(32, 42) }; }
    const A = { x: 0, y: 0 };
    const pts = [A, { x: u.x, y: u.y }, { x: u.x + v.x, y: u.y + v.y }, { x: v.x, y: v.y }];
    const deg = randomChoice([-14, -8, 0, 6, 11, 17]);
    return pts.map((P) => rot(P, deg));
};
const NOMS = ['A', 'B', 'C', 'D'];
const VB = { w: 300, h: 200 };
const BANDEAU = 22;   // hauteur d'une ligne de mention « (AB) // (DC) » sous la figure

/** Traits d'égalité au milieu de [a b] (n traits). */
const traits = (fig, a, b, n) => {
    const M = milieu(a, b); const d = dist(a, b) || 1; const ux = (b.x - a.x) / d; const uy = (b.y - a.y) / d; const nx = -uy; const ny = ux;
    for (let k = 0; k < n; k++) {
        const t = (k - (n - 1) / 2) * 5; const cx = M.x + ux * t; const cy = M.y + uy * t;
        fig.brut(`<line class="tk" x1="${(cx + nx * 5).toFixed(1)}" y1="${(cy + ny * 5).toFixed(1)}" x2="${(cx - nx * 5).toFixed(1)}" y2="${(cy - ny * 5).toFixed(1)}" stroke="#0f172a" stroke-width="1.8"/>`);
    }
};
const PLUS_PRECIS = { sansplus: ['rectangle', 'losange', 'carre'], rectangle: ['carre'], losange: ['carre'], carre: [] };
// taux d'allure trompeuse par conclusion : la seule nature plus précise qu'un rectangle ou un losange est le carré,
// un taux trop fort noierait la planche sous les carrés
const TAUX_TROMPEUSE = { sansplus: 0.7, rectangle: 0.5, losange: 0.5, carre: 0 };

/**
 * Figure codée d'un motif. Le dessin a AU MOINS la nature conclue (plus précise sept fois sur dix
 * pour « rien de plus », une fois sur deux pour rectangle et losange : allure trompeuse).
 */
const figure = (motif, opt = {}) => {
    // Allure trompeuse (revue 2026-09-10, A2, doctrine du 1080) : selon le taux ci-dessus, le dessin est PLUS précis
    // que ce que le codage prouve (un vrai carré codé seulement « un angle droit » → réponse rectangle).
    // Le dessin ne contredit jamais le codage : toute nature plus précise possède les propriétés codées.
    const plusPrecis = PLUS_PRECIS[motif.conclusion];
    const natureDessin = plusPrecis.length && Math.random() < TAUX_TROMPEUSE[motif.conclusion] ? randomChoice(plusPrecis) : motif.conclusion;
    const pts = sommets(natureDessin);
    const noms = opt.noms || NOMS;
    const c = motif.codes;
    const avecDiag = !!(c.diagEgales || c.diagPerp || motif.extra === 'milieu' || motif.extra === 'diagonales');
    const codesCotes = [];
    // côtés consécutifs : deux traits si les demi-diagonales portent déjà un trait (pas de confusion AB = AO)
    if (c.consecEgaux) { const n = c.diagEgales ? 2 : 1; codesCotes.push([0, 1, n], [0, 3, n]); }
    if (motif.extra === 'opposes') { codesCotes.push([0, 1, 1], [3, 2, 1], [1, 2, 2], [0, 3, 2]); }
    const { fig, P } = dessineQuad(pts, VB, `1123_${motif.id}_${randInt(1, 999)}`, {
        noms, diagonales: avecDiag, codesCotes, droits: c.angleDroit ? [0] : [],
    });
    const O = milieu(P[0], P[2]);
    if (avecDiag) {
        const a1 = Math.atan2(P[2].y - O.y, P[2].x - O.x); const a2 = Math.atan2(P[1].y - O.y, P[1].x - O.x);
        let am = (a1 + a2) / 2; if (Math.abs(a1 - a2) > Math.PI) am += Math.PI;
        // pour un angle droit codé en O, l'étiquette part dans le secteur opposé
        if (c.diagPerp) am += Math.PI;
        fig.brut(`<text x="${(O.x + 14 * Math.cos(am)).toFixed(1)}" y="${(O.y + 14 * Math.sin(am) + 5).toFixed(1)}" font-family="sans-serif" font-size="14" fill="#DC2626" text-anchor="middle">O</text>`);
    }
    if (c.diagEgales) { traits(fig, P[0], O, 1); traits(fig, P[2], O, 1); traits(fig, P[1], O, 1); traits(fig, P[3], O, 1); }
    if (c.diagPerp) codageDroitInterieur(fig, O, P[2], P[1], { couleur: '#DC2626', c: 9 });
    if (motif.extra === 'milieu') { traits(fig, P[0], O, 1); traits(fig, P[2], O, 1); traits(fig, P[1], O, 2); traits(fig, P[3], O, 2); }
    // parallélisme ÉCRIT sous la figure (décision prof 2026-09-10), jamais fléché sur les côtés
    const mentions = motif.extra === 'paralleles' ? [`(${noms[0]}${noms[1]}) // (${noms[3]}${noms[2]})`, `(${noms[1]}${noms[2]}) // (${noms[0]}${noms[3]})`] : [];
    let svg = fig.render();
    if (mentions.length) {
        const H = VB.h + BANDEAU * mentions.length;
        svg = svg.replace(`viewBox="0 0 ${VB.w} ${VB.h}" width="${VB.w}" height="${VB.h}"`, `viewBox="0 0 ${VB.w} ${H}" width="${VB.w}" height="${H}"`);
        const textes = mentions.map((m, k) => `<text class="mp" x="${VB.w / 2}" y="${VB.h + BANDEAU * k + 12}" font-family="sans-serif" font-size="17" font-weight="600" fill="#0f172a" text-anchor="middle">${m}</text>`).join('');
        svg = svg.replace('</svg>', `${textes}</svg>`);
    }
    svg = svg.replace('<svg ', `<svg data-motif="${motif.id}" data-conclusion="${motif.conclusion}" data-codes="${Object.keys(c).join(',')}" data-dessin="${natureDessin}" data-paralleles="${mentions.join('|')}" `);
    return { svg, motif, natureDessin };
};

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** La nature dite quand le codage prouve `vraie` : refuser de conclure, conclure avec trop peu, rectangle et losange échangés (un carré dit rectangle ou losange : rien). */
const codeNature = (vraie, dite, dessin) => {
    if (vraie === dite) return null;
    if (dite === 'sansplus') return 'refus_du_codage';
    if (vraie === 'sansplus' || dite === 'carre') return 'hypothese_manquante';
    // un code par sens (lot 2, 04/10/2026, docs/SFD_CODES_ERREUR.md) ; relecture du 04/10 (E10) : sur un dessin
    // de carré, « rectangle » peut venir du dessin (un carré est un rectangle) : deux histoires, pas de nom
    if (vraie === 'losange' && dite === 'rectangle') return dessin === 'carre' ? null : 'losange_pris_pour_rectangle';
    if (vraie === 'rectangle' && dite === 'losange') return 'rectangle_losange_confondus';
    return null;
};
const NATURE_DE_NOM = Object.fromEntries(Object.entries(NOM).map(([k, v]) => [v, k]));
const CLE_DE_PHRASE = Object.fromEntries(Object.entries(PHRASE).map(([k, v]) => [v, k]));
const NATURE_PROPRIETE = { diagEgales: 'rectangle', angleDroit: 'rectangle', diagPerp: 'losange', consecEgaux: 'losange' };
/** La propriété k citée quand le codage porte `cles` : une seule des deux du carré, une propriété non codée de la même nature, ou une de l'autre nature. */
const codePropriete = (cles, k) => {
    if (cles.length === 2) return cles.includes(k) ? 'hypothese_manquante' : 'lit_la_figure_pas_le_codage';
    if (NATURE_PROPRIETE[k] === NATURE_PROPRIETE[cles[0]]) return 'lit_la_figure_pas_le_codage';
    // une propriété de l'autre nature : un code par sens (lot 2, 04/10/2026, docs/SFD_CODES_ERREUR.md)
    return NATURE_PROPRIETE[cles[0]] === 'losange' ? 'losange_pris_pour_rectangle' : 'rectangle_losange_confondus';
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refN = { v: null }; const refP = { v: null }; const refR = { v: null };

// ─── Carte 1 : quelle est sa nature ? ──────────────────────────────────────

const construireNature = () => {
    // nature tirée d'abord (équilibre des quatre conclusions), puis un motif de cette nature
    const nature = randomChoice(['rectangle', 'losange', 'carre', 'sansplus']);
    const motif = randomChoice(parNature(nature));
    const F = figure(motif);
    const options = shuffle(['un rectangle', 'un losange', 'un carré', 'on ne peut rien affirmer de plus']);
    const payload = makeQcm(options, NOM[nature], '<p>ABCD est un parallélogramme. D\'après le codage, que peut-on affirmer de plus précis ?</p>', { ...opts(L_NATURE), elements_additionnels: imgElement(F.svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType: nature };
    payload._meta.params = { motif: motif.id, conclusion: nature, dessin: F.natureDessin, signature: `${motif.id}-${F.natureDessin}` };
    // chaque nature fausse nommée par rapport à celle que prouve le codage
    const erreurs = options.filter((o) => o !== NOM[nature]).map((o) => ({ code: codeNature(nature, NATURE_DE_NOM[o], F.natureDessin), valeur: o })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genNature = () => antiRepeat(construireNature, refN);

// ─── Carte 2 : la propriété utilisée ───────────────────────────────────────

const construirePropriete = () => {
    const motif = randomChoice(PARTICULIERS);
    const F = figure(motif);
    const cles = Object.keys(motif.codes);
    const bonne = cles.length === 1 ? PHRASE[cles[0]] : PHRASE2[cles.join('+')];
    // leurres : les propriétés NON codées ; pour un carré, aussi une seule des deux codées (vraie mais insuffisante)
    const nonCodees = Object.keys(PHRASE).filter((k) => !motif.codes[k]);
    const pool = cles.length === 2 ? [...nonCodees.map((k) => PHRASE[k]), ...cles.map((k) => PHRASE[k])] : nonCodees.map((k) => PHRASE[k]);
    const options = shuffle([bonne, ...shuffle(pool).slice(0, 3)]);
    const nom = NOM[motif.conclusion];
    const payload = makeQcm(options, bonne, `<p>ABCD est un parallélogramme. D'après le codage, c'est ${nom}. Qu'est-ce qui, dans le codage, permet de l'affirmer ?</p>`, { ...opts(L_PROPRIETE), elements_additionnels: imgElement(F.svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType: motif.conclusion };
    payload._meta.params = { motif: motif.id, conclusion: motif.conclusion, signature: `${motif.id}-${[...options].sort().join('|')}` };
    // chaque propriété fausse (une seule) nommée par rapport à ce que porte le codage
    const erreurs = options.filter((o) => o !== bonne).map((o) => ({ code: codePropriete(cles, CLE_DE_PHRASE[o]), valeur: o })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genPropriete = () => antiRepeat(construirePropriete, refP);

// ─── Carte 1 bis : quelle est sa nature ? (planche de quatre, même carte que le QCM) ──

const planche = (figs) => {
    const cols = 2; const rows = Math.ceil(figs.length / cols);
    const w = 210; const h = 165; const W = cols * w; const H = rows * h;
    let g = `<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>`;
    figs.forEach((f, i) => {
        const vbm = f.svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/); const vw = Number(vbm[1]); const vh = Number(vbm[2]);
        const contenu = f.svg.replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');
        const cc = i % cols; const r = Math.floor(i / cols);
        const sc = Math.min((w - 8) / vw, (h - 26) / vh); const tx = cc * w + (w - sc * vw) / 2; const ty = r * h + 22 + (h - 26 - sc * vh) / 2;
        g += `<g transform="translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${sc.toFixed(4)})">${contenu}</g>`;
        g += `<text x="${cc * w + w / 2}" y="${r * h + 16}" font-family="system-ui,sans-serif" font-size="15" font-weight="800" fill="#1e3a8a" text-anchor="middle">Figure ${f.label}</text>`;
    });
    const data = figs.map((f) => `${f.label}:${f.motif.id}:${f.motif.conclusion}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" data-figures="${data}">${g}</svg>`;
};

const construireRetrouver = () => {
    const cible = randomChoice(['rectangle', 'losange', 'carre']);
    const bon = randomChoice(parNature(cible));
    // les autres : jamais de la nature cherchée, ni un carré quand on cherche un rectangle ou un losange (P15)
    const autresNatures = cible === 'carre' ? ['rectangle', 'losange', 'sansplus'] : [cible === 'rectangle' ? 'losange' : 'rectangle', 'sansplus', 'sansplus'];
    const leurres = autresNatures.map((n) => randomChoice(n === 'sansplus' ? SANSPLUS : parNature(n)));
    // deux « rien de plus » distincts
    if (leurres[1] && leurres[2] && leurres[1].id === leurres[2].id) leurres[2] = randomChoice(SANSPLUS.filter((m) => m.id !== leurres[1].id));
    // figures numérotées 1 à 4 : chacune garde ses sommets A, B, C, D (les mentions de parallélisme les nomment)
    const lettres = ['1', '2', '3', '4'];
    const motifs = shuffle([bon, ...leurres]);
    const figs = motifs.map((m, i) => ({ ...figure(m), label: lettres[i] }));
    const bonne = figs.find((f) => f.motif.conclusion === cible).label;
    const payload = makeQcm(lettres, bonne, `<p>Voici quatre parallélogrammes codés. D'après son codage, lequel est certainement ${NOM[cible]} ?</p>`, { ...opts(L_NATURE), elements_additionnels: imgElement(planche(figs), 'large') });
    // options dans l'ordre 1-4 ; la clé de correction est la LETTRE de position (A-D), pas le numéro (revue 2026-09-10, A1)
    payload.contenu.choix = lettres; payload.contenu.bonne_reponse = 'ABCD'[lettres.indexOf(bonne)];
    payload.contenu = { ...payload.contenu, sousType: cible };
    payload._meta.params = { cible, bon: bon.id, leurres: leurres.map((m) => m.id), signature: `${cible}-${motifs.map((m) => m.id).join(',')}` };
    // une autre figure prise pour la nature cherchée : même règle que le QCM de la nature
    const erreurs = figs.filter((f) => f.label !== bonne).map((f) => ({ code: codeNature(f.motif.conclusion, cible), valeur: f.label })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genRetrouver = () => antiRepeat(construireRetrouver, refR);

// ─── Carte 4 : vrai ou faux ? (sur un parallélogramme codé) ────────────────

let derniereEmpreinteVf1123 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const nature = randomChoice(['rectangle', 'losange', 'carre', 'sansplus']);
        const motif = randomChoice(parNature(nature));
        const F = figure(motif);
        const c = motif.codes;
        // ligne fausse sur la nature : jamais une nature INCLUSE dans la conclusion (un carré est aussi un rectangle et un losange)
        const RIEN = 'D\'après le codage, on ne peut rien affirmer de plus que « ABCD est un parallélogramme ».';
        const autreNature = randomChoice(['rectangle', 'losange', 'carre'].filter((n) => n !== nature));
        const fauxNature = nature === 'carre' ? RIEN : nature === 'sansplus' ? `D'après le codage, ABCD est ${NOM[autreNature]}.` : (Math.random() < 0.5 ? `D'après le codage, ABCD est ${NOM[autreNature]}.` : RIEN);
        const familles = [
            { f: 'nature', vrai: nature === 'sansplus' ? RIEN : `D'après le codage, ABCD est ${NOM[nature]}.`, faux: fauxNature },
            { f: 'diag_egales', vrai: c.diagEgales ? 'Les diagonales sont codées de même longueur.' : 'Le codage ne dit pas que les diagonales ont la même longueur.', faux: c.diagEgales ? 'Le codage ne dit pas que les diagonales ont la même longueur.' : 'Les diagonales sont codées de même longueur.' },
            { f: 'diag_perp', vrai: c.diagPerp ? 'Les diagonales sont codées perpendiculaires.' : 'Le codage ne dit pas que les diagonales sont perpendiculaires.', faux: c.diagPerp ? 'Le codage ne dit pas que les diagonales sont perpendiculaires.' : 'Les diagonales sont codées perpendiculaires.' },
            { f: 'angle', vrai: c.angleDroit ? 'Un angle droit est codé.' : 'Aucun angle droit n\'est codé.', faux: c.angleDroit ? 'Aucun angle droit n\'est codé.' : 'Un angle droit est codé.' },
            { f: 'consec', vrai: c.consecEgaux ? 'Deux côtés consécutifs sont codés de même longueur.' : 'Le codage ne dit pas que deux côtés consécutifs ont la même longueur.', faux: c.consecEgaux ? 'Le codage ne dit pas que deux côtés consécutifs ont la même longueur.' : 'Deux côtés consécutifs sont codés de même longueur.' },
        ];
        // la famille « nature » est toujours présente, trois familles de lecture parmi quatre
        const retenues = [familles[0], ...shuffle(familles.slice(1)).slice(0, 3)];
        const nbFaux = randInt(1, 3);
        const ordre = shuffle(retenues); const lignes = ordre.map((fa, i) => (i < nbFaux ? { l: fa.faux, v: false, f: fa.f } : { l: fa.vrai, v: true, f: fa.f }));
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1123) continue;
        derniereEmpreinteVf1123 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>ABCD est un parallélogramme codé. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(F.svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { motif: motif.id, conclusion: nature, dessin: F.natureDessin, nbFaux };
        // la nature fausse comme au QCM ; les lignes de lecture : nier le codage, ou lui faire dire ce qu'il ne dit pas
        const codeFaux = {
            nature: codeNature(nature, fauxNature === RIEN ? 'sansplus' : autreNature, F.natureDessin),
            diag_egales: c.diagEgales ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
            diag_perp: c.diagPerp ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
            angle: c.angleDroit ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
            consec: c.consecEgaux ? 'refus_du_codage' : 'lit_la_figure_pas_le_codage',
        };
        const erreurs = melange.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1123 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : les parallélogrammes particuliers, vrai ou faux ? (pur rappel) ─

const POOL_RAPPEL = [
    { l: 'Un parallélogramme dont les diagonales ont la même longueur est un rectangle.', v: true, f: 'rect_diag' },
    { l: 'Un parallélogramme dont les diagonales ont la même longueur est un losange.', v: false, f: 'rect_diag', code: 'rectangle_losange_confondus' },
    { l: 'Un parallélogramme dont les diagonales sont perpendiculaires est un losange.', v: true, f: 'los_diag' },
    { l: 'Un parallélogramme dont les diagonales sont perpendiculaires est un rectangle.', v: false, f: 'los_diag', code: 'losange_pris_pour_rectangle' },
    { l: 'Un parallélogramme dont les diagonales sont perpendiculaires et de même longueur est un carré.', v: true, f: 'carre_diag' },
    { l: 'Un parallélogramme dont les diagonales se coupent en leur milieu est forcément un carré.', v: false, f: 'carre_diag', code: 'hypothese_manquante' },
    { l: 'Un parallélogramme qui a un angle droit est un rectangle.', v: true, f: 'rect_angle' },
    { l: 'Pour qu\'un parallélogramme soit un rectangle, il faut coder ses quatre angles droits.', v: false, f: 'rect_angle', code: 'refus_du_codage' },
    { l: 'Un parallélogramme qui a deux côtés consécutifs de même longueur est un losange.', v: true, f: 'los_cotes' },
    { l: 'Un parallélogramme qui a deux côtés opposés de même longueur est un losange.', v: false, f: 'los_cotes', code: 'opposes_consecutifs_confondus' },
    { l: 'Un carré est à la fois un rectangle et un losange.', v: true, f: 'inclusion' },
    { l: 'Un rectangle est toujours un carré.', v: false, f: 'inclusion', code: 'inclusion_inversee' },
    { l: 'Dans un rectangle, les diagonales se coupent en leur milieu et ont la même longueur.', v: true, f: 'rect_prop' },
    { l: 'Dans un rectangle, les diagonales sont toujours perpendiculaires.', v: false, f: 'rect_prop', code: 'cas_particulier_generalise' },
    { l: 'Dans un losange, les diagonales sont perpendiculaires.', v: true, f: 'los_prop' },
    { l: 'Dans un losange, les diagonales ont toujours la même longueur.', v: false, f: 'los_prop', code: 'cas_particulier_generalise' },
];

let dernieresLignesRappel1123 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1123 = [];
        if (lignes.some((it) => dernieresLignesRappel1123.includes(it.l))) continue;
        dernieresLignesRappel1123 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les parallélogrammes particuliers, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1123 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNature,
    propriete_qcm_unique: genPropriete,
    nature_planche_qcm_unique: genRetrouver,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', planche_qcm_unique: 'QCM planche', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'propriete', label: L_PROPRIETE, formatsList: buildFormats('propriete') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    nature_qcm_unique: ['hypothese_manquante', 'refus_du_codage'],
    vf_tableau_vf: ['refus_du_codage', 'hypothese_manquante'],
    // relecture du 04/10/2026 : losange_pris_pour_rectangle n'est gardé que sur les cartes où le juge l'a lu
    rappel_tableau_vf: ['cas_particulier_generalise', 'refus_du_codage', 'losange_pris_pour_rectangle'],
    // le lot 2 (04/10/2026) coupe rectangle_losange_confondus en deux sens : les deux restent retirés ici
    nature_planche_qcm_unique: ['hypothese_manquante', 'rectangle_losange_confondus', 'losange_pris_pour_rectangle'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { MOTIFS, lireCodage, NOM, PHRASE, figure, sommets, POOL_RAPPEL };
